import { useEffect, useMemo, useRef, useState } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { CardSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import { Printer } from "lucide-react"
import { fr } from "../../locales/fr"
import { getAccountingSummary } from "../../services/accountingService"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function monthBounds() {
  const now = new Date()
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const pad = (n) => String(n).padStart(2, "0")
  const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  return { from: ymd(from), to: ymd(to) }
}

function formatMoney(n) {
  const x = Number(n)
  if (Number.isNaN(x)) return "—"
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "MAD" }).format(x)
}

function formatPeriodLine(fromIso, toIso) {
  const opts = { day: "numeric", month: "long", year: "numeric" }
  const a = new Date(`${fromIso}T12:00:00`)
  const b = new Date(`${toIso}T12:00:00`)
  return `${a.toLocaleDateString("fr-FR", opts)} — ${b.toLocaleDateString("fr-FR", opts)}`
}

export default function Accounting() {
  const chartRef = useRef(null)
  const defaults = useMemo(() => monthBounds(), [])
  const [from, setFrom] = useState(defaults.from)
  const [to, setTo] = useState(defaults.to)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getAccountingSummary(from, to)
      setData(res)
    } catch (e) {
      console.error(e)
      setError(e.response?.data?.error || "Erreur")
      setData(null)
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    const onBeforePrint = () => {
      const chart = chartRef.current
      if (chart && typeof chart.resize === "function") {
        chart.resize()
        chart.update("none")
      }
    }
    window.addEventListener("beforeprint", onBeforePrint)
    return () => window.removeEventListener("beforeprint", onBeforePrint)
  }, [])

  const handlePrint = () => {
    requestAnimationFrame(() => {
      window.print()
    })
  }

  const chartData = useMemo(() => {
    if (!data?.monthly?.length) return null
    return {
      labels: data.monthly.map((m) => m.month),
      datasets: [
        {
          label: fr.accountingPage.revenue,
          data: data.monthly.map((m) => m.revenue),
          backgroundColor: "hsl(142 76% 36% / 0.75)",
          borderColor: "hsl(142 76% 36%)",
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: fr.accountingPage.expenses,
          data: data.monthly.map((m) => m.expenses),
          backgroundColor: "hsl(0 72% 51% / 0.65)",
          borderColor: "hsl(0 72% 51%)",
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    }
  }, [data])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "hsl(0 0% 64%)" },
      },
    },
    scales: {
      x: {
        grid: { color: "hsl(0 0% 18%)" },
        ticks: { color: "hsl(0 0% 64%)" },
      },
      y: {
        grid: { color: "hsl(0 0% 18%)" },
        ticks: { color: "hsl(0 0% 64%)" },
      },
    },
  }

  const expenseTotal = data?.expenseTotal ?? 0
  const byType = data?.expensesByType ?? []

  return (
    <div id="accounting-print-root" className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{fr.accountingPage.title}</h1>
          <p className="text-muted-foreground">{fr.accountingPage.subtitle}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 print:hidden"
          aria-label={fr.accountingPage.printAria}
          title={fr.accountingPage.printAria}
          onClick={handlePrint}
        >
          <Printer className="mr-2 h-4 w-4" aria-hidden />
          {fr.accountingPage.printOrPdf}
        </Button>
      </div>

      <p className="hidden border-b border-border pb-2 text-sm text-foreground print:block">
        <span className="font-medium">{fr.accountingPage.periodLabel} :</span>{" "}
        {formatPeriodLine(from, to)}
      </p>

      <Card className="print:hidden">
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{fr.accountingPage.periodFrom}</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[180px]" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{fr.accountingPage.periodTo}</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[180px]" />
          </div>
          <Button type="button" onClick={load}>
            {fr.accountingPage.apply}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-3 print:hidden">
          {[1, 2, 3].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {fr.accountingPage.revenue}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatMoney(data.revenueTotal)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {data.paymentCount} {fr.accountingPage.paymentCount.toLowerCase()}
                  {typeof data.productSaleCount === "number" && data.productSaleCount > 0
                    ? ` · ${fr.accountingPage.productSalesInPeriod(data.productSaleCount)}`
                    : ""}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {fr.accountingPage.expenses}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {formatMoney(data.expenseTotal)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {data.expenseCount} {fr.accountingPage.expenseCount.toLowerCase()}
                </p>
              </CardContent>
            </Card>
            <Card className="sm:col-span-2 lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {fr.accountingPage.net}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p
                  className={`text-3xl font-bold ${
                    data.net >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                  }`}
                >
                  {formatMoney(data.net)}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{fr.accountingPage.hint}</p>
              </CardContent>
            </Card>
          </div>

          {chartData && data.monthly.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{fr.accountingPage.monthlyChart}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72 w-full print:h-80">
                  <Bar ref={chartRef} data={chartData} options={chartOptions} />
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>{fr.accountingPage.byType}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {byType.length === 0 ? (
                <div className="p-6">
                  <EmptyState title={fr.accountingPage.emptyTypes} />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{fr.accountingPage.typeName}</TableHead>
                      <TableHead className="text-right">{fr.accountingPage.amount}</TableHead>
                      <TableHead className="text-right">{fr.accountingPage.share}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byType.map((row) => {
                      const pct =
                        expenseTotal > 0 ? Math.round((row.total / expenseTotal) * 1000) / 10 : 0
                      return (
                        <TableRow key={row.typeId}>
                          <TableCell>
                            <span className="inline-flex items-center gap-2">
                              {row.color && (
                                <span
                                  className="inline-block h-2.5 w-2.5 rounded-full border border-border"
                                  style={{ backgroundColor: row.color }}
                                />
                              )}
                              {row.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatMoney(row.total)}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">{pct}%</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}
